import { useCallback, useEffect } from 'react'

import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useNavigate, useParams, useSearch } from '@tanstack/react-router'
import { useFormData } from 'brief-form'
import { Request } from 'commons/utils/request'
import { Toast } from 'ui/components'
import { useConfirmation } from 'ui/hooks'

import { handlerServerError } from '@orbit/errors'

import { CONTACTS_PATH } from '../../constants'
import { emptyContact, useContactItem } from '../../hooks'
import { messages } from '../../messages'
import { ContactForm } from '../../types/contact'

const request = (contact: ContactForm) => {
  const fn = contact.id ? Request.put : Request.post
  return fn(Request.urls.bank.contact, contact)
}
export const useContactManage = () => {
  const { contactId } = useParams({ strict: false })

  const { data, isFetching: isContactFetching, error } = useContactItem(+(contactId || 0))

  useEffect(() => {
    if (error) {
      throw error
    }
  }, [error])

  const formData = useFormData<ContactForm>({ initialValue: data?.form || emptyContact })

  // edit. contact loaded, change form value
  useEffect(() => {
    if (!data?.form) {
      return
    }
    formData.set({ reset: true, value: data?.form })
  }, [data?.form])

  const client = useQueryClient()

  const navigate = useNavigate({ from: '/contacts/new' })

  const search = useSearch({ strict: false })

  const { mutate: saveContact, isPending: isContactFormSaving } = useMutation<ContactForm, Error, ContactForm>({
    mutationFn: (opts) => request(opts),
    onSuccess: async (responseContact, payload) => {
      const successMessage =
        data?.form && data?.form.id ? messages['bankContacts.updateSuccess'] : messages['bankContacts.createSuccess']
      Toast.successSmall(successMessage)

      if (!search.returnUrl) {
        if (responseContact.id) {
          await navigate({
            to: '/contacts/$contactId',
            params: { contactId: responseContact.id.toString() }
          })
        } else {
          await navigate({
            to: '/contacts/$contactId',
            params: { contactId: payload.id.toString() }
          })
        }
      }

      // this row for flow "Company Page > Create New Contact > Back to Company Page with Opened Edit Contact dialog
      if (search.successReturnUrl && search.successReturnUrl.includes('contact/add')) {
        await navigate({ to: `${search.successReturnUrl + responseContact.id}?contactName=${payload.lastName}` })
      } else {
        await navigate({ to: search.returnUrl as never })
      }
    },
    onError: (e) => handlerServerError(e)
  })

  const { mutate: deleteContact } = useMutation<void, Error, { id: number }>({
    mutationFn: ({ id }) => Request.delete(Request.urls.bank.contact, { id }),
    onSuccess: async () => {
      await client.invalidateQueries({ queryKey: ['contact-list'] })
      navigate({ to: CONTACTS_PATH })
      Toast.successSmall(messages['bankContacts.deleteSuccess'])
    },
    onError: (error) => handlerServerError(error)
  })

  const { open: openDeleteConfirmation, Confirmation: DeleteConfirmation } = useConfirmation({
    message: messages['bankContacts.deleteConfirmation']({
      contactFirstName: data?.form?.firstName,
      contactLastName: data?.form?.lastName
    }),
    onConfirm: () => data?.form && deleteContact({ id: data?.form.id }),
    confirmationButtonText: messages['bankContacts.delete'],
    isConfirmationNegative: true
  })

  const handleSubmitContactForm = useCallback(async () => {
    if (!formData.validate({ updateFields: true }).valid) {
      return
    }

    await saveContact(formData.config.value)
  }, [saveContact, formData])

  const returnUrlForCancel = search.cancelReturnUrl || CONTACTS_PATH

  return {
    formData,
    subTitle: data?.form && data?.form.id ? messages['bankContacts.editTitle'] : messages['bankContacts.createTitle'],
    isContactFetching,
    isContactFormSaving,
    showBio: data?.showBio,
    DeleteConfirmation,
    returnUrlForCancel,
    onDelete: openDeleteConfirmation,
    onSubmit: handleSubmitContactForm
  }
}
